import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Download, User, GraduationCap, Search, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts';
import { apiCall } from '../lib/api';
import { toast } from 'sonner';

export const ReportCardsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState('Term 1');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        // Teachers and admins can see all students
        if (user?.user_type === 'teacher' || user?.user_type === 'admin') {
          const data = await apiCall('GET', '/users?user_type=student', null, token);
          setStudents(data);
        } 
        // Parents can only see their linked children
        else if (user?.user_type === 'parent') {
          const data = await apiCall('GET', '/parent/children', null, token);
          setStudents(data.map(child => ({
            id: child.child_id,
            first_name: child.child_name.split(' ')[0],
            last_name: child.child_name.split(' ').slice(1).join(' '),
            email: child.child_email,
            grade: child.child_grade
          })));
        }
        // Students can only see their own
        else if (user?.user_type === 'student') {
          setStudents([user]);
        }
      } catch (err) {
        console.error('Failed to fetch students:', err);
        toast.error('Failed to load students');
      }
      setLoading(false);
    };
    
    if (token) fetchStudents();
  }, [token, user]);

  const handleDownloadReportCard = async (studentId, studentName) => {
    setDownloading(studentId);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${API_URL}/api/grades/report-card/${studentId}?semester=${encodeURIComponent(selectedSemester)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Report_Card_${studentName.replace(/\s+/g, '_')}_${selectedSemester.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Report card downloaded for ${studentName}`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(error.message || 'Failed to download report card');
    }
    setDownloading(null);
  };

  const filteredStudents = students.filter(student => {
    const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase()) ||
           student.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           student.grade?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="py-8 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen" data-testid="report-cards-page">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#002147] flex items-center gap-3">
            <FileText className="text-[#FF6B00]" />
            Report Cards
          </h1>
          <p className="text-[#002147]/70 mt-1">
            {user?.user_type === 'student' 
              ? 'Download your academic report card'
              : 'Generate and download student report cards'}
          </p>
        </div>

        {/* Filters */}
        <Card className="bg-white mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              {(user?.user_type === 'teacher' || user?.user_type === 'admin') && (
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search by name, email, or grade..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent"
                    data-testid="search-students-input"
                  />
                </div>
              )}
              
              {/* Semester Select */}
              <div className="w-full md:w-48">
                <Select value={selectedSemester} onValueChange={setSelectedSemester}>
                  <SelectTrigger data-testid="semester-select">
                    <SelectValue placeholder="Select Term" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Term 1">Term 1</SelectItem>
                    <SelectItem value="Term 2">Term 2</SelectItem>
                    <SelectItem value="Term 3">Term 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Students List */}
        {filteredStudents.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-12 text-center">
              <User className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-500">
                {searchQuery ? 'No students match your search' : 'No students found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredStudents.map((student) => (
              <Card key={student.id} className="bg-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#002147] rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">
                          {student.first_name?.[0]}{student.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-[#002147]">
                          {student.first_name} {student.last_name}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span>{student.email}</span>
                          {student.grade && (
                            <Badge variant="outline" className="text-xs">
                              <GraduationCap size={12} className="mr-1" />
                              {student.grade}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      className="bg-[#FF6B00] hover:bg-[#E65C00]"
                      onClick={() => handleDownloadReportCard(student.id, `${student.first_name} ${student.last_name}`)}
                      disabled={downloading === student.id}
                      data-testid={`download-report-${student.id}`}
                    >
                      {downloading === student.id ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={16} />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download className="mr-2" size={16} />
                          Download Report Card
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-white mt-6">
          <CardContent className="p-6">
            <h3 className="font-semibold text-[#002147] mb-2">About Report Cards</h3>
            <p className="text-sm text-gray-600 mb-4">
              Report cards include academic grades, GPA calculation, attendance summary, and grading scale reference.
              The PDF is formatted for printing and includes signature lines for teachers, principal, and parents.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">Academic Grades</Badge>
              <Badge variant="secondary">GPA Calculation</Badge>
              <Badge variant="secondary">Attendance Summary</Badge>
              <Badge variant="secondary">Grading Scale</Badge>
              <Badge variant="secondary">Signature Lines</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportCardsPage;
